/**
 * Relative strength / weakness vs a benchmark.
 *
 * Two complementary numbers:
 *
 * 1) Period outperformance (UI “%” column):
 *    windowExcessPct = ticker % change − benchmark % change over the window
 *    “+1.2%” = beat the benchmark by 1.2 points over this timeframe.
 *
 * 2) Volume-weighted strength (UI 0–100 score + sort + meter):
 *    For each aligned bar: excess_i = tickerReturn_i − benchmarkReturn_i
 *    weight_i = ticker volume (floor 1)
 *    scorePct = 100 × Σ(w·excess) / Σ(w)
 *    High-volume bars count more, so a quiet drift scores lower than the same
 *    move on heavy volume.
 *
 * Score dial is 0…100 (50 = in line). Meter uses RS_VOLUME_METER_SCALE because
 * avg per-bar excess does not grow with timeframe the way period excess does.
 */

/** @typedef {{ slight: number, medium: number, very: number }} RsBandThresholds */
/** @typedef {{ knee: number, cap: number }} RsMeterScale */

/**
 * vs-benchmark % bands by Market Overview timeframe.
 * Short windows need tighter cutoffs; multi-month windows expect larger spreads.
 */
export const RS_WINDOW_BANDS = {
  '1D': { slight: 0.35, medium: 0.9, very: 1.8 },
  '1W': { slight: 1, medium: 2.5, very: 5 },
  '1M': { slight: 2, medium: 5, very: 10 },
  '3M': { slight: 4, medium: 10, very: 18 },
  '6M': { slight: 6, medium: 14, very: 25 },
  YTD: { slight: 6, medium: 14, very: 28 },
  '1Y': { slight: 8, medium: 18, very: 35 },
};

/** Log-meter for volume-weighted avg per-bar excess % (scorePct). */
export const RS_VOLUME_METER_SCALE = { knee: 0.15, cap: 5 };

/**
 * Tighter dial for the Relative Strength study. The live-table scale keeps
 * typical 21-day / 12-week mean excess in a ~45–70 band; this one still
 * saturates only on large moves, but lets ordinary strength and weakness
 * use more of 0–100.
 */
export const RS_STUDY_METER_SCALE = { knee: 0.08, cap: 1.2 };

/** Log-meter scales matched to typical period-outperformance magnitudes per TF. */
export const RS_METER_SCALE = {
  '1D': { knee: 0.3, cap: 4 },
  '1W': { knee: 0.8, cap: 10 },
  '1M': { knee: 1.5, cap: 18 },
  '3M': { knee: 3, cap: 30 },
  '6M': { knee: 4, cap: 40 },
  YTD: { knee: 4, cap: 45 },
  '1Y': { knee: 5, cap: 50 },
};

const DEFAULT_BANDS = RS_WINDOW_BANDS['1D'];
const DEFAULT_METER = RS_METER_SCALE['1D'];

/**
 * @param {string} [timeframeKey]
 * @returns {RsBandThresholds}
 */
export function getRelativeStrengthBands(timeframeKey) {
  return RS_WINDOW_BANDS[timeframeKey] || DEFAULT_BANDS;
}

/**
 * @param {string} [timeframeKey]
 * @returns {RsMeterScale}
 */
export function getRelativeStrengthMeterScale(timeframeKey) {
  return RS_METER_SCALE[timeframeKey] || DEFAULT_METER;
}

function barKey(timestamp, unit) {
  if (!timestamp) return null;
  if (unit === 'Daily' || unit === 'Weekly' || unit === 'Monthly') {
    return String(timestamp).substring(0, 10);
  }
  const ms = Date.parse(timestamp);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

function unixSec(stamp, time) {
  const fromRaw = Number(stamp?.timeSec);
  if (Number.isFinite(fromRaw)) return fromRaw;
  if (typeof time === 'number' && Number.isFinite(time)) return time;
  const ms = Date.parse(stamp) || Date.parse(String(time));
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

function parseBar(raw, unit) {
  if (!raw) return null;
  const stamp = raw.TimeStamp || raw.TradeTime || raw.time;
  const time = barKey(stamp, unit);
  const close = parseFloat(raw.Close ?? raw.close);
  const volume = parseFloat(raw.TotalVolume ?? raw.Volume ?? raw.volume) || 0;
  if (time == null || !Number.isFinite(close) || close <= 0) return null;
  const timeSec = unixSec(raw.timeSec != null ? raw : stamp, time);
  return {
    time,
    timeSec,
    closeTimeSec: Number.isFinite(raw.closeTimeSec) ? raw.closeTimeSec : timeSec,
    close,
    volume: Math.max(volume, 0),
  };
}

/**
 * Normalize broker bars and sort ascending by time.
 * @param {Array} bars
 * @param {string} unit
 * @returns {Array<{time: string|number, close: number, volume: number}>}
 */
export function normalizeBarsForRelativeStrength(bars, unit = 'Daily') {
  if (!Array.isArray(bars)) return [];
  return bars
    .map((b) => parseBar(b, unit))
    .filter(Boolean)
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
}

/**
 * Align ticker + benchmark on shared timestamps; keep the most recent `limit` pairs.
 */
export function alignBarsByTime(tickerBars, benchmarkBars, limit = Infinity) {
  const benchMap = new Map(benchmarkBars.map((b) => [b.time, b]));
  const aligned = [];
  for (const t of tickerBars) {
    const b = benchMap.get(t.time);
    if (!b) continue;
    aligned.push({ ticker: t, benchmark: b });
  }
  if (!Number.isFinite(limit) || aligned.length <= limit) return aligned;
  return aligned.slice(aligned.length - limit);
}

/**
 * @param {object} opts
 * @param {Array} opts.tickerBars - raw or normalized bars
 * @param {Array} opts.benchmarkBars
 * @param {string} [opts.unit='Daily']
 * @param {number} [opts.minBars=10]
 * @param {number} [opts.barsback] - max aligned bars to use (equal sample size)
 * @returns {{
 *   ok: boolean,
 *   reason?: string,
 *   score: number|null,
 *   scorePct: number|null,
 *   windowExcessPct: number|null,
 *   tickerReturnPct: number|null,
 *   benchmarkReturnPct: number|null,
 *   barsUsed: number,
 *   totalWeight: number,
 * }}
 */
export function computeRelativeStrength({
  tickerBars,
  benchmarkBars,
  unit = 'Daily',
  minBars = 10,
  barsback,
} = {}) {
  const empty = {
    ok: false,
    score: null,
    scorePct: null,
    windowExcessPct: null,
    tickerReturnPct: null,
    benchmarkReturnPct: null,
    barsUsed: 0,
    totalWeight: 0,
  };

  const looksNormalized = (bars) =>
    bars?.[0] && Number.isFinite(bars[0].close) && bars[0].time != null;
  const tickerNorm = looksNormalized(tickerBars)
    ? tickerBars
    : normalizeBarsForRelativeStrength(tickerBars, unit);
  const benchNorm = looksNormalized(benchmarkBars)
    ? benchmarkBars
    : normalizeBarsForRelativeStrength(benchmarkBars, unit);

  const limit = Number.isFinite(barsback) ? barsback : Infinity;
  const aligned = alignBarsByTime(tickerNorm, benchNorm, limit);

  if (aligned.length < minBars) {
    return {
      ...empty,
      reason: `Need at least ${minBars} aligned bars (got ${aligned.length})`,
      barsUsed: aligned.length,
    };
  }

  let weightedSum = 0;
  let totalWeight = 0;
  for (let i = 1; i < aligned.length; i += 1) {
    const prevT = aligned[i - 1].ticker;
    const curT = aligned[i].ticker;
    const prevB = aligned[i - 1].benchmark;
    const curB = aligned[i].benchmark;
    const rT = (curT.close - prevT.close) / prevT.close;
    const rB = (curB.close - prevB.close) / prevB.close;
    if (!Number.isFinite(rT) || !Number.isFinite(rB)) continue;
    const weight = Math.max(curT.volume, 1);
    weightedSum += weight * (rT - rB);
    totalWeight += weight;
  }

  if (totalWeight <= 0) {
    return { ...empty, reason: 'No volume-weighted samples', barsUsed: aligned.length };
  }

  const score = weightedSum / totalWeight;
  const firstT = aligned[0].ticker.close;
  const lastT = aligned[aligned.length - 1].ticker.close;
  const firstB = aligned[0].benchmark.close;
  const lastB = aligned[aligned.length - 1].benchmark.close;
  const tickerReturn = (lastT - firstT) / firstT;
  const benchReturn = (lastB - firstB) / firstB;
  const windowExcess = tickerReturn - benchReturn;

  return {
    ok: true,
    score,
    scorePct: score * 100,
    windowExcessPct: windowExcess * 100,
    tickerReturnPct: tickerReturn * 100,
    benchmarkReturnPct: benchReturn * 100,
    barsUsed: aligned.length,
    totalWeight,
  };
}

/**
 * Map an excess % into -1..1 for meters with log compression.
 *
 *   meter = sign(x) * log1p(|x| / knee) / log1p(cap / knee)
 *
 * Pass RS_VOLUME_METER_SCALE for volume-weighted scorePct, or a timeframe key /
 * getRelativeStrengthMeterScale(tf) for period window excess.
 */
export function strengthMeterValue(excessPct, scaleOrOpts = RS_VOLUME_METER_SCALE) {
  const scale = typeof scaleOrOpts === 'string'
    ? getRelativeStrengthMeterScale(scaleOrOpts)
    : scaleOrOpts;
  const knee = scale.knee ?? RS_VOLUME_METER_SCALE.knee;
  const cap = scale.cap ?? RS_VOLUME_METER_SCALE.cap;
  if (!Number.isFinite(excessPct) || knee <= 0 || cap <= 0) return 0;
  const denom = Math.log1p(cap / knee);
  if (!(denom > 0)) return 0;
  const n = Math.log1p(Math.abs(excessPct) / knee) / denom;
  const clamped = Math.max(0, Math.min(1, n));
  if (excessPct === 0) return 0;
  return Math.sign(excessPct) * clamped;
}

/**
 * Strength score on a 0…100 dial from volume-weighted scorePct (50 = flat vs bench).
 */
export function relativeStrengthScore(volumeScorePct, scaleOrOpts = RS_VOLUME_METER_SCALE) {
  if (!Number.isFinite(volumeScorePct)) return null;
  const meter = strengthMeterValue(volumeScorePct, scaleOrOpts);
  return Math.round(((meter + 1) / 2) * 100);
}

const RS_HEAT_NEUTRAL_LOW = 45;
const RS_HEAT_NEUTRAL_HIGH = 55;
/** Cap darken so extremes stay readable (never near-black). */
const RS_HEAT_MAX_DARKEN = 0.22;
const RS_HEAT_NEAR_LIGHTEN = 0.32;

function parseHexColor(hex) {
  const h = String(hex || '').replace('#', '').trim();
  if (h.length !== 6 || Number.isNaN(Number.parseInt(h, 16))) return null;
  return {
    r: Number.parseInt(h.slice(0, 2), 16),
    g: Number.parseInt(h.slice(2, 4), 16),
    b: Number.parseInt(h.slice(4, 6), 16),
  };
}

function mixRgb(a, b, t) {
  const u = Math.max(0, Math.min(1, t));
  return {
    r: Math.round(a.r + (b.r - a.r) * u),
    g: Math.round(a.g + (b.g - a.g) * u),
    b: Math.round(a.b + (b.b - a.b) * u),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Combined-strength trend icon: mid-band is sideways, not an up arrow.
 * Returns 'up' | 'down' | null (flat).
 */
export function relativeStrengthTrendAlign(score) {
  if (!Number.isFinite(score)) return null;
  if (score >= RS_HEAT_NEUTRAL_HIGH) return 'up';
  if (score <= RS_HEAT_NEUTRAL_LOW) return 'down';
  return null;
}

/**
 * Heat-map text color for a 0–100 relative-strength score.
 * Neutral white when 45 < score < 55; greens intensify above, reds below.
 * Extremes darken modestly and never approach black.
 *
 * @param {number|null|undefined} score
 * @param {{ success?: string, danger?: string, text?: string }} [colors]
 * @returns {string} CSS color
 */
export function relativeStrengthScoreHeatColor(score, colors = {}) {
  const neutral = colors.text || '#FFFFFF';
  if (!Number.isFinite(score)) return neutral;
  if (score > RS_HEAT_NEUTRAL_LOW && score < RS_HEAT_NEUTRAL_HIGH) return neutral;

  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };

  if (score >= RS_HEAT_NEUTRAL_HIGH) {
    const t = Math.min(1, Math.max(0, (score - RS_HEAT_NEUTRAL_HIGH) / (100 - RS_HEAT_NEUTRAL_HIGH)));
    const base = parseHexColor(colors.success) || { r: 6, g: 180, b: 138 };
    const near = mixRgb(base, white, RS_HEAT_NEAR_LIGHTEN);
    const far = mixRgb(base, black, RS_HEAT_MAX_DARKEN);
    return rgbToHex(mixRgb(near, far, t));
  }

  const t = Math.min(1, Math.max(0, (RS_HEAT_NEUTRAL_LOW - score) / RS_HEAT_NEUTRAL_LOW));
  const base = parseHexColor(colors.danger) || { r: 220, g: 38, b: 38 };
  const near = mixRgb(base, white, RS_HEAT_NEAR_LIGHTEN);
  const far = mixRgb(base, black, RS_HEAT_MAX_DARKEN);
  return rgbToHex(mixRgb(near, far, t));
}

/**
 * User-facing vs-benchmark string.
 * compact: "+1.20%"  |  full: "+1.20% vs SPY"
 */
export function formatVsBenchmark(vsBenchmarkPct, benchmark = 'SPY', { compact = false } = {}) {
  if (!Number.isFinite(vsBenchmarkPct)) return '—';
  const sign = vsBenchmarkPct >= 0 ? '+' : '';
  const pct = `${sign}${vsBenchmarkPct.toFixed(2)}%`;
  if (compact) return pct;
  const sym = String(benchmark || '').trim().toUpperCase() || 'benchmark';
  return `${pct} vs ${sym}`;
}

/**
 * Qualitative band for vs-benchmark % over the selected timeframe.
 * Thresholds scale with timeframeKey so ±1% can be "very strong" on 1D
 * but only "slightly" on 1Y.
 *
 * @param {number|null|undefined} vsBenchmarkPct
 * @param {string} [timeframeKey='1D']
 * @returns {{ key: string, label: string, tone: 'bullish'|'bearish'|'neutral' }}
 */
export function classifyRelativeStrength(vsBenchmarkPct, timeframeKey = '1D') {
  if (!Number.isFinite(vsBenchmarkPct)) {
    return { key: 'unknown', label: '—', tone: 'neutral' };
  }
  const { slight, medium, very } = getRelativeStrengthBands(timeframeKey);
  if (vsBenchmarkPct >= very) {
    return { key: 'very_strong', label: 'Very strong', tone: 'bullish' };
  }
  if (vsBenchmarkPct >= medium) {
    return { key: 'medium_strong', label: 'Medium strong', tone: 'bullish' };
  }
  if (vsBenchmarkPct >= slight) {
    return { key: 'slightly_strong', label: 'Slightly strong', tone: 'bullish' };
  }
  if (vsBenchmarkPct > -slight) {
    return { key: 'neutral', label: 'Neutral', tone: 'neutral' };
  }
  if (vsBenchmarkPct > -medium) {
    return { key: 'slightly_weak', label: 'Slightly weak', tone: 'bearish' };
  }
  if (vsBenchmarkPct > -very) {
    return { key: 'medium_weak', label: 'Medium weak', tone: 'bearish' };
  }
  return { key: 'very_weak', label: 'Very weak', tone: 'bearish' };
}

/**
 * Sort by volume-weighted strength (scorePct). Falls back to period excess.
 */
export function sortByRelativeStrength(rows, direction = 'strength') {
  const valueOf = (row) => {
    if (Number.isFinite(row?.scorePct)) return row.scorePct;
    if (Number.isFinite(row?.windowExcessPct)) return row.windowExcessPct;
    return Number.NEGATIVE_INFINITY;
  };
  return [...rows].sort((a, b) => {
    const aScore = valueOf(a);
    const bScore = valueOf(b);
    if (direction === 'weakness') {
      return aScore - bScore;
    }
    return bScore - aScore;
  });
}
